/**
 * 統括表・明細表 API（/api/cost-summaries）。F-EXP-06 / S-58
 */
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { downloadFile } from '@/lib/download';

export const SUBTITLES = ['（概算払）', '（最終精算）', '（実績）'] as const;
export const QUARTERS = ['選択無し', '第1四半期', '第2四半期', '第3四半期', '第4四半期', '通期'] as const;
export const PAYMENT_STATUSES = ['支払済', '未支払', '両方'] as const;
export const PATTERNS = ['出港毎', '1隻1行'] as const;
export const ORDER1 = ['参加船名簿、出港日の順', '出港日、参加船名簿の順'] as const;
export const ORDER2 = ['消費税対象を分ける', '消費税対象を分けない'] as const;
export const TAX_ORDERS = ['有り→無しの順', '無し→有りの順'] as const;

export interface CostOptions {
  fiscalYear: number;
  businessOperatorId: number | null;
  subtitle: string;
  departureFrom: string;
  departureTo: string;
  surveyFrom: string;
  surveyTo: string;
  quarter: (typeof QUARTERS)[number];
  scheduledFrom: string;
  scheduledTo: string;
  paidFrom: string;
  paidTo: string;
  paymentStatus: (typeof PAYMENT_STATUSES)[number];
  pattern: (typeof PATTERNS)[number];
  order1: (typeof ORDER1)[number];
  order2: (typeof ORDER2)[number];
  taxOrder: (typeof TAX_ORDERS)[number];
  excludeVesselIds: number[];
}

export interface CostRow {
  no: number | null;
  vesselId: number;
  vesselName: string | null;
  fishingMethods: string | null;
  tonnage: number | null;
  horsepower: string | null;
  startDate: string;
  endDate: string;
  zones: string;
  crew: number | string;
  workDays: number;
  manDays: number;
  wage: number;
  charter: number;
  fuel: number;
  lube: number;
  targetRatePercent: number | null;
  targetRateLabel: string;
  targetAmount: number | null;
  total: number;
  remarks: string;
  dailyAverage: number;
  fuelDays: number | null;
  voyageReportIds: number[];
}

export interface CostTotals {
  crew: number | '';
  workDays: number;
  manDays: number;
  wage: number;
  charter: number;
  fuel: number;
  lube: number;
  targetAmount: number | null;
  total: number;
}

export interface CostResult {
  conditions: { surveyFrom: string; surveyTo: string; departureFrom: string; departureTo: string };
  groups: { key: string; tonClass: string; taxGroup: '有り' | '無し' | null; label: string; rows: CostRow[]; subtotal: CostTotals }[];
  middles: { label: string; taxGroup: '有り' | '無し'; totals: CostTotals }[];
  total: CostTotals;
  summary: {
    wageUnit: number;
    manDays: number;
    wageAmount: number;
    charter: Record<string, { unit: number; days: number; amount: number }>;
    fuelAmount: number;
    lubeAmount: number;
    consumablesAmount: number;
    targetAmount: number;
    total: number;
  };
  isActual: boolean;
}

/** 空文字の任意条件は送らない */
function body(o: CostOptions) {
  return Object.fromEntries(Object.entries({ ...o, businessOperatorId: o.businessOperatorId ?? undefined }).filter(([, v]) => v !== '' && v !== undefined));
}

export function usePreviewCost() {
  return useMutation({ mutationFn: (o: CostOptions) => api.post<CostResult>('/cost-summaries/preview', body(o)) });
}

export function useExportCost() {
  return useMutation({
    mutationFn: (input: { options: CostOptions; form: '別記1' | '別記2' }) =>
      downloadFile('/cost-summaries/export', { method: 'POST', body: { ...body(input.options), form: input.form } }),
  });
}
